import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import SettingsValidator from '../entities/settingsValidators';
import SettingsSection from '../entities/configuration/settingsSection';
import ProgressIndicator from '../progressIndicator';

const settingsValidator = new SettingsValidator();

type Props = {};

const NewAgentInstance = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [agentDefs, setAgentDefs] = useState<any[]>([]);
  const [agentDef, setAgentDef] = useState<any>();
  const [agentDefId, setAgentDefId] = useState<any>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [agentInstance, setAgentInstance] = useState<any>();

  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const getAgentDefs = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getAgentDefs();
        setAgentDefs(response);

        const queryDefId = searchParams.get('defId');
        if (queryDefId && response.some((ad: any) => ad.id === queryDefId)) {
          setAgentDefId(queryDefId);
          setAgentDef(response.find((ad: any) => ad.id === queryDefId));
          const instance = await apiContext.getNewAgentInstance(queryDefId);
          setAgentInstance(instance);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    getAgentDefs();
  }, [searchParams]);

  const onDefChange = async (agentDefId: string) => {
    setAgentDefId(agentDefId);
    setAgentDef(agentDefs.find((ad) => ad.id === agentDefId));
    if (agentDefId) {
      const instance = await apiContext.getNewAgentInstance(agentDefId);
      setAgentInstance(instance);
    } else {
      setAgentInstance(null);
    }
  };

  const onSettingChange = (settingName: string, value: any) => {
    setAgentInstance({ ...agentInstance, settings: { ...agentInstance.settings, [settingName]: value } });
  };

  const submitEntity = async () => {
    const localErrors = {} as any;
    let hasError = false;

    if (!agentDefId) {
      localErrors['agentDefId'] = 'Required';
      hasError = true;
    }
    if (!name) {
      localErrors['name'] = 'Required';
      hasError = true;
    }

    const [settingErrors = {}, hasSettingsError = false] = settingsValidator.validateSettings(
      agentDef?.settingDefs || [],
      agentInstance?.settings || {},
      t,
    );

    hasError = hasError || hasSettingsError;
    setErrors({ ...localErrors, ...settingErrors });
    setHasErrors(hasError);

    if (!hasError) {
      const { id, tenantId, entityType, entityDefId, settings = {} } = agentInstance;
      try {
        setLoading(true);
        const response = await apiContext.createAgentInstance({
          id,
          tenantId,
          entityType,
          entityDefId,
          name,
          description,
          settings,
        });
        navigate(`/agentInstance/${agentDefId}/${response.id}`, {
          state: { from: '/instances?tab=agentInstances' },
        });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const close = () => {
    navigate('/instances?tab=agentInstances');
  };

  if (loading && agentDefs.length === 0) {
    return <ProgressIndicator />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          New Agent Instance
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5, lg: 4 }}>
            <Stack spacing={2.5}>
              <SelectField
                label="Agent type"
                required
                options={agentDefs.map((ad) => ({ value: ad.id, label: ad.name }))}
                value={agentDefId}
                autoFocus
                onChange={onDefChange}
                error={errors.agentDefId}
                fullWidth
              />
              <TextField
                label="Name"
                value={name}
                required
                error={errors.name}
                onChange={setName}
                type="text"
                fullWidth
              />
              <TextField
                label="Description"
                value={description}
                multiline
                maxRows={3}
                error={errors.description}
                onChange={setDescription}
                fullWidth
              />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 7, lg: 8 }}>
            {agentInstance && agentDef && (
              <Box sx={{ pl: { md: 2 }, borderLeft: { md: 1 }, borderColor: 'divider', height: '100%' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 2, display: 'block' }}
                >
                  Agent Settings
                </Typography>
                <Stack spacing={2}>
                  {agentDef.settingDefs
                    .filter((section: any) => !section.system)
                    .map((section: any) => (
                      <SettingsSection
                        key={section.sectionName}
                        entityDefId={agentDef.id}
                        settings={agentInstance.settings}
                        settingsSection={section}
                        errors={errors}
                        onChange={onSettingChange}
                      />
                    ))}
                </Stack>
                {hasErrors && (
                  <Box sx={{ mt: 2 }}>
                    <ErrorMessage error={{ message: 'Please correct the errors in the fields below.' }} />
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        <Button size="small" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          disabled={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={submitEntity}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
        >
          {loading ? 'Creating...' : 'Create Instance'}
        </Button>
      </Stack>
    </Box>
  );
};

export default NewAgentInstance;
